import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { coreServicesApi } from '../lib/api/core-services.js';

export interface User {
  id: string;
  discordId: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  isGodMode: boolean;
  isAdmin: boolean;
}

export interface Guild {
  id: string;
  name: string;
  isOwner: boolean;
}

export interface AuthState {
  user: User | null;
  guilds: Guild[];
  isLoading: boolean;
  isAuthenticated: boolean;
}

export interface AuthContextType extends AuthState {
  login: () => void;
  devLogin: () => void;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    guilds: [],
    isLoading: true,
    isAuthenticated: false,
  });

  // Check for existing authentication on mount
  useEffect(() => {
    const isDevAuthed = localStorage.getItem('dev_auth') === 'true';
    if (isDevAuthed) {
      setStubbedState();
    } else {
      checkAuthStatus();
    }
  }, []);

  const setStubbedState = () => {
    setState({
      user: {
        id: '123456789',
        discordId: '123456789',
        username: 'dev_user',
        displayName: 'Developer (uwu)',
        avatarUrl: 'https://cdn.discordapp.com/embed/avatars/0.png',
        isGodMode: true,
        isAdmin: true,
      },
      guilds: [
        { id: '987654321', name: 'Dev Server', isOwner: true },
      ],
      isLoading: false,
      isAuthenticated: true,
    });
  };

  // Check authentication status
  const checkAuthStatus = async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true }));

      const response = await coreServicesApi.auth.refresh();

      setState({
        user: response.user,
        guilds: response.guilds || [],
        isLoading: false,
        isAuthenticated: true,
      });
    } catch (error) {
      console.debug('Not authenticated:', error);
      setState({
        user: null,
        guilds: [],
        isLoading: false,
        isAuthenticated: false,
      });
    }
  };

  // Redirect to Discord OAuth login
  const login = () => {
    // Generate a random state for CSRF protection
    const state = Math.random().toString(36).substring(2);
    localStorage.setItem('oauth_state', state);

    // Redirect to Core Services OAuth endpoint
    window.location.href = `/api/auth/discord/login?state=${state}`;
  };

  const devLogin = () => {
    localStorage.setItem('dev_auth', 'true');
    setStubbedState();
  };

  // Logout user
  const logout = async () => {
    try {
      await coreServicesApi.auth.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear state regardless of API call success
      setState({
        user: null,
        guilds: [],
        isLoading: false,
        isAuthenticated: false,
      });

      // Clear any stored OAuth state
      localStorage.removeItem('oauth_state');
      localStorage.removeItem('dev_auth');

      // Redirect to login
      window.location.href = '/login';
    }
  };

  // Refresh authentication status
  const refreshAuth = async () => {
    await checkAuthStatus();
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    devLogin,
    logout,
    refreshAuth,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Higher-order component for protected routes
export interface RequireAuthProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function RequireAuth({ children, fallback }: RequireAuthProps) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return fallback || (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Authentication Required</h2>
          <p className="text-gray-400 mb-6">Please log in to access this page.</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
